import { useParams, useNavigate } from 'react-router-dom';
import { articles, stepInfo } from '../../mocks/articles';
import { useState } from 'react';
import RequestFormModal from '../home/components/RequestFormModal';
import StepPageSEO from '../../components/feature/StepPageSEO';

export default function StepPage() {
  const { stepId } = useParams<{ stepId: string }>();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const step = stepId ? stepInfo[stepId] : null;
  const stepArticles = articles.filter((a) => a.step === stepId);

  if (!step) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8]">
        <p className="text-[#6B6B6B] mb-6">ステップが見つかりませんでした</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 rounded-full bg-[#8FA882] text-white text-sm font-semibold hover:bg-[#7A9570] transition-colors cursor-pointer whitespace-nowrap"
        >
          ホームに戻る
        </button>
      </div>
    );
  }

  const allSteps = Object.entries(stepInfo);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {stepId && <StepPageSEO stepId={stepId} stepLabel={step.label} />}
      {/* Top Nav */}
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E8DDD0]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/2c450daa-ab1c-4449-ac53-d1ae0dbc5040.png"
              alt="ロゴ"
              className="h-7 w-auto object-contain"
            />
            <span className="text-sm font-semibold text-[#2C2C2C] whitespace-nowrap hidden md:block">
              鳥取ではじめての家づくり
            </span>
          </a>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-[#6B6B6B] hover:text-[#8FA882] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              ホームへ
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="px-4 py-2 rounded-full bg-[#D4A96A] text-white text-xs font-bold hover:bg-[#C09050] transition-colors cursor-pointer whitespace-nowrap"
            >
              資料請求（無料）
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <div className="py-12 md:py-16 bg-white border-b border-[#E8DDD0]">
        <div className="max-w-6xl mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-[#A0A0A0] mb-6">
            <a href="/" className="hover:text-[#8FA882] transition-colors">ホーム</a>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#6B6B6B]">家づくりのステップ</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#6B6B6B]">STEP {stepId}</span>
          </nav>

          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: step.bgColor }}
            >
              <i className={`${step.icon} text-xl`} style={{ color: step.color }} />
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest mb-0.5" style={{ color: step.color }}>
                STEP {stepId}
              </p>
              <h1 className="text-2xl md:text-3xl font-bold text-[#2C2C2C]">
                {step.label}
              </h1>
            </div>
          </div>
          <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-xl">
            「{step.label}」に関する記事を一覧でまとめています。<br />
            家づくりの{step.label}ステップで役立つ情報をぜひご確認ください。
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Articles Grid */}
          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#6B6B6B]">
                <strong className="text-[#2C2C2C] font-bold">{stepArticles.length}件</strong>の記事
              </p>
            </div>

            {stepArticles.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <i className="ri-article-line text-4xl text-[#D0C8C0] mb-4 block" />
                <p className="text-[#A0A0A0] text-sm">このステップの記事は準備中です</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stepArticles.map((article) => (
                  <article
                    key={article.id}
                    onClick={() => navigate(`/articles/${article.id}`)}
                    className="bg-white rounded-2xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="mx-4 mt-4 rounded-xl overflow-hidden h-44">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2.5 py-0.5 rounded-full bg-[#E8F0E6] text-[#5A7A52] text-[10px] font-semibold">
                          {article.category}
                        </span>
                        <span className="text-[#A0A0A0] text-[10px]">{article.date}</span>
                        <span className="text-[#A0A0A0] text-[10px] ml-auto flex items-center gap-0.5">
                          <i className="ri-time-line text-xs" />
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="text-[#2C2C2C] text-sm font-bold leading-snug line-clamp-2 mb-2 group-hover:text-[#8FA882] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-[#6B6B6B] text-xs leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-1 text-[#8FA882] text-xs font-semibold">
                        <span>記事を読む</span>
                        <i className="ri-arrow-right-line" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </main>

          {/* Sidebar */}
          <aside className="w-full lg:w-64 flex-shrink-0 flex flex-col gap-6">
            {/* All Steps Nav */}
            <div className="bg-white rounded-2xl p-6">
              <p className="text-xs font-bold text-[#A0A0A0] uppercase tracking-wide mb-4">
                すべてのステップ
              </p>
              <div className="flex flex-col gap-2">
                {allSteps.map(([num, s]) => {
                  const isActive = num === stepId;
                  const count = articles.filter((a) => a.step === num).length;
                  return (
                    <a
                      key={num}
                      href={`/steps/${num}`}
                      className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors no-underline ${
                        isActive
                          ? 'bg-[#E8F0E6]'
                          : 'hover:bg-[#F5F1ED]'
                      }`}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={
                          isActive
                            ? { backgroundColor: s.color, color: '#fff' }
                            : { backgroundColor: '#F5F1ED', color: '#A0A0A0' }
                        }
                      >
                        {num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-xs font-semibold leading-tight"
                          style={{ color: isActive ? s.color : '#6B6B6B' }}
                        >
                          {s.label}
                        </p>
                        <p className="text-[10px] text-[#A0A0A0] mt-0.5">{count}件の記事</p>
                      </div>
                      {isActive && (
                        <i className="ri-arrow-right-s-line text-sm flex-shrink-0" style={{ color: s.color }} />
                      )}
                    </a>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div className="rounded-2xl overflow-hidden">
              <div className="relative p-6">
                <div className="absolute inset-0">
                  <img
                    src="https://public.readdy.ai/ai/img_res/d238a768799ccc1f605cd0c2ecb7a4ad.jpg"
                    alt=""
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-[#3D5C38]/85" />
                </div>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-white/90 text-[10px] font-semibold mb-3">
                    <i className="ri-gift-line" />
                    完全無料
                  </div>
                  <h3 className="text-white font-bold text-base leading-snug mb-2">
                    まずは無料で<br />資料請求してみる
                  </h3>
                  <p className="text-white/80 text-xs leading-relaxed mb-4">
                    間取り・費用・工務店情報を一括でお届け。しつこい営業は一切なし。
                  </p>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="w-full py-3 rounded-full bg-[#D4A96A] text-white text-sm font-bold hover:bg-[#C09050] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    資料請求はこちら →
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <RequestFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
